import React, { useState, useEffect } from "react";
import {
  Table,
  Typography,
  Button,
  Modal,
  message,
  Input,
  Upload,
  Space,
  Form,
} from "antd";
import { PlusOutlined, UploadOutlined, PaperClipOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import EditIcon from "../../../../../ui/EditIcon";
import { opConfirmModal } from "../../../opConfirmModal";
import {
  getExercisesAPI,
  addExerciseAPI,
  updateExerciseAPI,
  deleteExercisesAPI,
} from "../../../../../../services/outpatient.services";
import { getUploadUrlApi } from "../../../../../../services/common.services";
import axios from "axios";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import {
  opExerciseNameFormRules,
  opExerciseTypeFormRules,
  doctorExerciseFrequencyRules,
  doctorExerciseIntensityRules,
} from "../OpExercise/exerciseFieldValidation";
import useIsMobile from "../../../../../../hooks/useIsMobile";

const ExercisePage = () => {
  const isMobile = useIsMobile();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);

  const [selectedExercise, setSelectedExercise] = useState(null);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [uploadValues, setUploadValues] = useState({
    videoFileList: [],
    videoPreviewURL: "",
    s3Path: null,
    isLink: false,
  });

  useEffect(() => {
    fetchExercises();
  }, []);

  useEffect(() => {
    return () => {
      message.destroy("video_upload");
    };
  }, []);

  // If the component stays mounted (e.g., switching tabs inside the page),
  // the unmount cleanup won't run. Clear the "Processing video..." toast
  // immediately after we have an uploaded file.
  useEffect(() => {
    if (uploadValues?.videoFileList?.length) {
      message.destroy("video_upload");
    }
  }, [uploadValues?.videoFileList?.length]);

  useEffect(() => {
    return () => {
      // Cleanup the object URL when component unmounts
      if (
        uploadValues.videoPreviewURL &&
        uploadValues.videoPreviewURL.startsWith("blob:")
      ) {
        URL.revokeObjectURL(uploadValues.videoPreviewURL);
      }
    };
  }, [uploadValues.videoPreviewURL]);
  const isYouTube = (url) => {
    return /youtube\.com|youtu\.be/.test(url);
  };
  const convertGoogleDriveLink = (url) => {
    let fileId = null;
    const match1 = url.match(/\/d\/([^/]+)/);
    if (match1) fileId = match1[1];
    const match2 = url.match(/id=([^&]+)/);
    if (match2) fileId = match2[1];
    if (!fileId) return url;
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  };

  const getDriveEmbedURL = (url) => {
    let fileId = null;
    const match1 = url.match(/\/d\/([^/]+)/);
    if (match1) fileId = match1[1];
    const match2 = url.match(/id=([^&]+)/);
    if (match2) fileId = match2[1];
    if (!fileId) return null;
    return `https://drive.google.com/file/d/${fileId}/preview`;
  };

  const fetchExercises = async () => {
    setLoading(true);
    try {
      const res = await getExercisesAPI();
      setExercises(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load exercises");
    } finally {
      setLoading(false);
    }
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };
  const uploadProps = {
    beforeUpload: (file, fileList) => {
      const MAX_UPLOAD_SIZE = 30 * 1024 * 1024;

      let file_ext = file.name.split(".").slice(-1)[0].toLowerCase();
      const validVideoTypes = ["mp4", "mov", "avi", "m4v", "3gp"];

      const fileType = validVideoTypes.includes(file_ext);

      if (!fileType) {
        message.error(`${file.name} is not a valid video file type`);
        return Upload.LIST_IGNORE;
      }

      const totalSize = fileList.reduce((sum, f) => sum + f.size, 0);

      if (totalSize > MAX_UPLOAD_SIZE) {
        message.error("The uploading file reaches the maximum limit");
        setUploadFileLoading(true);
        return Upload.LIST_IGNORE;
      }

      setUploadFileLoading(false);

      // Call your existing handleVideoUpload
      handleVideoUpload(file);
      return false; // Prevent default upload behavior
    },
  };

  const handleVideoUpload = async (file) => {
    const MAX_UPLOAD_SIZE = 29 * 1024 * 1024;
    const loadingKey = "video_upload";
    message.loading({
      content: "Processing video...",
      key: loadingKey,
      duration: 0,
    });

    try {
      let uploadFile = file;

      if (file.size > MAX_UPLOAD_SIZE) {
        message.loading({
          content: "Compressing video...",
          key: loadingKey,
          duration: 0,
        });

        const ffmpeg = new FFmpeg();
        await ffmpeg.load();
        await ffmpeg.writeFile("input.mp4", await fetchFile(file));

        await ffmpeg.exec([
          "-i",
          "input.mp4",
          "-vcodec",
          "libx264",
          "-b:v",
          "800k",
          "-vf",
          "scale=1280:-1",
          "-preset",
          "fast",
          "output.mp4",
        ]);

        const data = await ffmpeg.readFile("output.mp4");
        const compressedBlob = new Blob([data.buffer], { type: file.type });
        uploadFile = new File([compressedBlob], `compressed-${file.name}`, {
          type: file.type,
        });
      }
      message.loading({
        content: "Uploading video...",
        key: loadingKey,
        duration: 0,
      });

      const res = await getUploadUrlApi(`path_key=${uploadFile.name}`);
      if (!res?.data) throw new Error("Failed to get upload URL");

      const formData = new FormData();
      Object.entries(res.data.fields).forEach(([key, value]) =>
        formData.append(key, value)
      );
      formData.append("file", file);

      await axios.post(res.data.url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Create a local preview URL for immediate viewing
      const localPreviewURL = URL.createObjectURL(file);

      const uploadedFile = {
        path: res.data.fields.key,
        type: file.type,
        url: localPreviewURL, // Use local object URL for preview
      };

      setUploadValues({
        videoFileList: [uploadedFile],
        videoPreviewURL: localPreviewURL, // Preview will work immediately
        s3Path: uploadedFile,
        isLink: false,
      });

      message.success({
        content: "Video uploaded successfully!",
        key: loadingKey,
        duration: 2,
      });
    } catch (err) {
      console.error(err);
      message.error({
        content: "Video upload failed!",
        key: loadingKey,
        duration: 3,
      });
    } finally {
      window.setTimeout(() => message.destroy(loadingKey), 3000);
    }

    return false;
  };

  const resetForm = () => {
    addForm.resetFields();
    editForm.resetFields();
    setUploadValues({
      videoFileList: [],
      videoPreviewURL: "",
      s3Path: null,
      isLink: false,
    });
  };

  const handleAddSubmit = async (formFields) => {
    try {
      const payload = {
        ...formFields,
        frequency: Number(formFields.frequency),
        intensity: Number(formFields.intensity),
        source_type: uploadValues.isLink ? "other_source" : "file",
        file: uploadValues.s3Path
          ? { path: uploadValues.s3Path.path, type: uploadValues.s3Path.type }
          : {},
        others_link: uploadValues.isLink ? uploadValues.videoPreviewURL : null,
      };
      await addExerciseAPI(payload);
      message.success("Exercise added successfully!");
      setAddModalVisible(false);
      resetForm();
      fetchExercises();
    } catch (err) {
      console.error(err);
      message.error("Failed to add exercise");
      throw err;
    }
  };

  const handleEditSubmit = async (formFields) => {
    try {
      const payload = {
        ...formFields,
        frequency: Number(formFields.frequency),
        intensity: Number(formFields.intensity),
      };
      await updateExerciseAPI(selectedExercise.id, payload);
      message.success("Exercise updated successfully!");
      setEditModalVisible(false);
      setSelectedExercise(null);
      resetForm();
      fetchExercises();
    } catch (err) {
      console.error(err);
      message.error("Failed to update exercise");
      throw err;
    }
  };

  const handleEdit = (exercise) => {
    setSelectedExercise(exercise);
    editForm.setFieldsValue({
      name: exercise.name,
      exercise_type: exercise.exercise_type,
      frequency: exercise.frequency,
      intensity: exercise.intensity,
    });
    setUploadValues({
      videoPreviewURL: exercise.s3_path?.url || exercise.others_link || "",
      isLink: !!exercise.others_link,
    });
    setEditModalVisible(true);
  };
  const getYouTubeEmbedURL = (url) => {
    if (!url) return "";
    const videoIdMatch = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]+)/
    );
    return videoIdMatch
      ? `https://www.youtube.com/embed/${videoIdMatch[1]}`
      : url;
  };

  const handleDelete = async (exercise) => {
    opConfirmModal({
      title: "Are you sure you want to delete this exercise?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteExercisesAPI(exercise.id);
          message.success("Exercise deleted successfully");
          fetchExercises();
        } catch (err) {
          console.error(err);
          message.error("Failed to delete exercise");
        }
      },
    });
  };

  const handleView = (exercise) => {
    setSelectedExercise(exercise);
    setUploadValues({
      videoPreviewURL: exercise.s3_path?.url || exercise.others_link || "",
      isLink: !!exercise.others_link,
    });
    setViewModalVisible(true);
  };
  const renderAddFormFields = () => (
    <Form form={addForm} layout="vertical" preserve={false}>
      <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
        Video Upload
      </Typography.Text>

      <div style={{ marginBottom: 16 }}>
        <Space
          style={{ marginBottom: 16, width: "100%" }}
          align="start"
          direction="vertical"
          size={12}
        >
          <Input
            placeholder="Paste link (optional)"
            prefix={<PaperClipOutlined />}
            style={{ width: "100%" }}
            value={uploadValues.isLink ? uploadValues.videoPreviewURL : ""}
            onChange={(e) => {
              let val = e.target.value;

              if (
                val.includes("drive.google.com") ||
                val.includes("docs.google.com")
              ) {
                val = convertGoogleDriveLink(val);
              }

              setUploadValues((prev) => ({
                ...prev,
                videoPreviewURL: val,
                isLink: !!val,
                s3Path: val
                  ? { path: null, type: "link", url: val }
                  : prev.s3Path,
                videoFileList: val ? [] : prev.videoFileList,
              }));
            }}
          />
          <Upload
            accept=".png,.jpg,.jpeg,.svg,.mp4,.mov,.avi,.pdf"
            {...uploadProps}
            maxCount={1}
            customRequest={dummyRequest}
            showUploadList={false}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{ width: "100%" }}
            >
              Record or Upload Video
            </Button>
          </Upload>
        </Space>
      </div>
      {uploadValues.videoPreviewURL && (
        <>
          {uploadValues.videoPreviewURL && (
            <>
              {uploadValues.videoPreviewURL.includes("drive.google.com") ? (
                <iframe
                  width="100%"
                  height="240"
                  src={getDriveEmbedURL(uploadValues.videoPreviewURL)}
                  allow="autoplay"
                  allowFullScreen
                  style={{ border: "none", marginBottom: 16 }}
                />
              ) : isYouTube(uploadValues.videoPreviewURL) ? (
                <iframe
                  width="100%"
                  height="240"
                  src={getYouTubeEmbedURL(uploadValues.videoPreviewURL)}
                  allow="autoplay"
                  allowFullScreen
                  style={{ border: "none", marginBottom: 16 }}
                />
              ) : (
                <video
                  width="100%"
                  height="240"
                  controls
                  src={uploadValues.videoPreviewURL}
                  style={{ marginBottom: 16 }}
                />
              )}
            </>
          )}
        </>
      )}

      <Form.Item label="Exercise Name" name="name" rules={opExerciseNameFormRules}>
        <Input placeholder="Exercise Name" />
      </Form.Item>

      <Form.Item
        label="Exercise Type"
        name="exercise_type"
        rules={opExerciseTypeFormRules}
      >
        <Input placeholder="Exercise Type" />
      </Form.Item>

      <Form.Item
        label="Frequency"
        name="frequency"
        rules={doctorExerciseFrequencyRules}
      >
        <Input type="number" placeholder="Frequency" />
      </Form.Item>

      <Form.Item
        label="Intensity"
        name="intensity"
        rules={doctorExerciseIntensityRules}
      >
        <Input type="number" placeholder="Intensity" />
      </Form.Item>
    </Form>
  );
  const renderEditFormFields = () => (
    <Form form={editForm} layout="vertical" preserve={false}>
      {uploadValues.videoPreviewURL ? (
        <video
          width="100%"
          height="240"
          controls
          src={uploadValues.videoPreviewURL}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginBottom: 16 }}
        >
          No video available for this exercise
        </Typography.Text>
      )}

      <Form.Item label="Exercise Name" name="name" rules={opExerciseNameFormRules}>
        <Input placeholder="Exercise Name" />
      </Form.Item>

      <Form.Item
        label="Exercise Type"
        name="exercise_type"
        rules={opExerciseTypeFormRules}
      >
        <Input placeholder="Exercise Type" />
      </Form.Item>

      <Space style={{ width: "100%" }} wrap>
        <Form.Item
          label="Frequency"
          name="frequency"
          rules={doctorExerciseFrequencyRules}
        >
          <Input type="number" placeholder="Frequency" />
        </Form.Item>
        <Form.Item
          label="Intensity"
          name="intensity"
          rules={doctorExerciseIntensityRules}
        >
          <Input type="number" placeholder="Intensity" />
        </Form.Item>
      </Space>
    </Form>
  );

  const columns = [
    { title: "Exercise Name", dataIndex: "name", key: "name" },
    {
      title: "Exercise Type",
      dataIndex: "exercise_type",
      key: "exercise_type",
    },
    { title: "Frequency", dataIndex: "frequency", key: "frequency" },
    { title: "Intensity", dataIndex: "intensity", key: "intensity" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            type="link"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="exercise-container">
      <Space
        style={{
          marginBottom: 16,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Typography.Title level={4}>Exercises</Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            resetForm();
            setAddModalVisible(true);
          }}
        >
          Add Exercise
        </Button>
      </Space>

      <Table
        dataSource={exercises}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 5 }}
      />

      <Modal
        title="Add Exercise"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          resetForm();
        }}
        onOk={() =>
          addForm.validateFields().then((values) => handleAddSubmit(values))
        }
        width={600}
      >
        {renderAddFormFields()}
      </Modal>
      <Modal
        title="Edit Exercise"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          resetForm();
        }}
        onOk={() =>
          editForm.validateFields().then((values) => handleEditSubmit(values))
        }
        width={600}
      >
        {renderEditFormFields()}
      </Modal>

      <Modal
        title="Exercise View"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={600}
      >
        {uploadValues.videoPreviewURL && (
          <>
            {uploadValues.videoPreviewURL.includes("drive.google.com") ? (
              <iframe
                width="100%"
                height="240"
                src={getDriveEmbedURL(uploadValues.videoPreviewURL)}
                allow="autoplay"
                allowFullScreen
                style={{ border: "none", marginBottom: 16 }}
              />
            ) : (
              <video
                width="100%"
                height="240"
                controls
                src={uploadValues.videoPreviewURL}
                style={{ marginBottom: 16 }}
              />
            )}
          </>
        )}
        <Typography.Text strong>Name:</Typography.Text>{" "}
        {selectedExercise?.name} <br />
        <Typography.Text strong>Type:</Typography.Text>{" "}
        {selectedExercise?.exercise_type} <br />
        <Typography.Text strong>Frequency:</Typography.Text>{" "}
        {selectedExercise?.frequency} <br />
        <Typography.Text strong>Intensity:</Typography.Text>{" "}
        {selectedExercise?.intensity} <br />
      </Modal>
    </div>
  );
};

export default ExercisePage;
